import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import keys from 'lodash/keys';
import pick from 'lodash/pick';
import { omit, set, unset, isEmpty } from 'lodash';
import { useDispatch } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { MessageBar, MessageBarType } from 'office-ui-fabric-react/lib/MessageBar';
import { useLocation } from 'react-router-dom';
import Link from '../Link';
import StackGrid from '../StackGrid';
import { createBundle, editBundle } from '../../actions/bundles';
import TextField from '../TextField';
import Dropdown from '../Dropdown';
import BundledProductList from '../BundledProductList';
import DatePicker from '../DatePicker';
import styles from './BundleForm.style';
import SubModule from '../SubModule';
import SubModuleHeader from '../SubModuleHeader';
import SubModuleContent from '../SubModuleContent';
import TextButton from '../../components/TextButton';
import { bundleTypes, dateValidationMessage } from '../../constants';

const BundleSchema = Yup.object().shape({
  bundleName: Yup.string().required('Required'),
  bundleId: Yup.string().required('Required'),
  bundleType: Yup.string().required('Required'),
  bundleDescription: Yup.string(),
  startDate: Yup.date().required('Required'),
  endDate: Yup.date().required('Required'),
  componentAllocationPercentage: Yup.array(
    Yup.object()
      .shape({
        allocationPercentage: Yup.number().min(0).max(100),
        isRoyaltyBearing: Yup.boolean().required('Required'),
        productId: Yup.string().required('Required'),
      })
      .required('Required'),
  ).required('Required'),
});

const BundleForm = React.forwardRef(
  ({ bundleId, onUpdate, onSuccess, showAllocationError, setShowAllocationError }, ref) => {
    const dispatch = useDispatch();
    const { bundleFormData } = useLocation();

    const [initialValues, setinitialValues] = useState({
      bundleName: '',
      bundleId: '',
      bundleType: '',
      startDate: '',
      endDate: '',
      componentAllocationPercentage: [],
    });
    const [products, setProducts] = useState([]);
    const [allProductsSelected, setAllProductsSelected] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [errorFields, setErrorFields] = useState(null);

    const componentAllocationKeys = {
      allocationPercentage: 0,
      isRoyaltyBearing: false,
      productId: '',
      tokenId: '',
    };

    const getKeyValueProducts = productData => {
      const productsForBundle = productData.map(product => {
        let item = product;
        if (!item.productsIds) {
          item = {
            name: product.name,
            tokenId: product.tokenId,
            isRoyaltyBearing: product?.isRoyaltyBearing || true,
            allocationPercentage: product?.allocationPercentage || 0,
            productsIds: product?.productsIds || [],
          };
          item.productsIds = product?.metadata?.productIds.map(id => {
            return {
              key: id,
              text: id,
            };
          });
          item.productId = product?.productId || item.productsIds[0].key;
        }
        return item;
      });
      return productsForBundle;
    };

    useEffect(() => {
      if (bundleFormData) {
        if (Array.isArray(bundleFormData.componentAllocationPercentage)) {
          const productsBundle = getKeyValueProducts(bundleFormData.componentAllocationPercentage);
          const updateBundle = { ...bundleFormData, componentAllocationPercentage: productsBundle };
          onUpdate(
            bundleFormData.componentAllocationPercentage.length > 0 &&
              isEmpty(bundleFormData.errorFields),
          );
          setProducts(productsBundle);
          setinitialValues(updateBundle);
        } else {
          setinitialValues(bundleFormData);
        }
        setErrorFields(bundleFormData.errorFields);
      }
    }, [bundleFormData]);

    const handleSelectAllButton = () => {
      const allProducts = products.map(item => item.tokenId);
      setSelectedProducts(allProducts);
      setAllProductsSelected(true);
    };

    const handleDeselectAllButton = () => {
      setSelectedProducts([]);
      setAllProductsSelected(false);
    };

    const handleProductSelect = prods => {
      setSelectedProducts(prods);
    };

    return (
      <Formik
        innerRef={ref}
        initialValues={initialValues}
        validateOnChange={false}
        validateOnBlur={false}
        enableReinitialize
        validationSchema={BundleSchema}
        onSubmit={async (values, { setErrors }) => {
          const submittedValues = omit(values, 'errorFields');
          submittedValues.componentAllocationPercentage =
            submittedValues.componentAllocationPercentage.map(prod => {
              const item = pick(prod, keys(componentAllocationKeys));
              item.allocationPercentage = prod.allocationPercentage || 0;
              return item;
            });
          let result;
          if (bundleId) {
            result = await dispatch(editBundle(submittedValues));
          }
          result = await dispatch(createBundle(submittedValues));
          if (result.error && result.payload.status === 409) {
            setErrors({
              form: `Bundle with name ${submittedValues.bundleName} or id ${submittedValues.bundleId} already exists`,
            });
          } else if (result.error && result.payload.message) {
            const errorMessage =
              result.payload.message === dateValidationMessage
                ? result.payload.message
                    .replace('endDate', 'Expiration Date')
                    .replace('ref:startDate', 'Effective Start Date')
                : result.payload.message;
            setErrors({ form: errorMessage });
          }

          // if no errors and
          if (!result.error) {
            onSuccess(get(result, `payload.entities.bundles.${result.payload.result}`));
          }
          return result;
        }}
      >
        {({ values, isSubmitting, errors, setFieldValue, setErrors }) => {
          const handleRemoveProduct = (productsToRemove, type) => {
            const updatedProducts = products.filter(
              product => !productsToRemove.includes(product.tokenId),
            );
            setFieldValue('componentAllocationPercentage', updatedProducts);
            setProducts(updatedProducts);
            if (type === 'removeSelected') {
              setSelectedProducts([]);
              setAllProductsSelected(false);
            }
            onUpdate(updatedProducts?.length > 0 && isEmpty(errors?.endDate));
          };
          const handleUpdatedProductList = (updatedProducts, type) => {
            // update productlist in the bundle
            setFieldValue('componentAllocationPercentage', updatedProducts);
            if (type === 'allocationValue') {
              setProducts(updatedProducts);
            }
          };

          // checking date change will result in error
          const handleDateFieldChange = async (name, selectedDate) => {
            const updatedErrors = omit(errors, name);
            const updatedDate = new Date(selectedDate);
            const expirationError = 'Expiration Date must be later than Effective Start Date';
            if (name === 'endDate' && updatedDate <= new Date(values?.startDate)) {
              setErrors({
                ...errors,
                [name]: expirationError,
              });
              set(updatedErrors, [name], expirationError);
            } else if (name === 'startDate' && updatedDate >= new Date(values?.endDate)) {
              setErrors({
                ...errors,
                endDate: expirationError,
              });
              set(updatedErrors, 'endDate', expirationError);
            } else if (
              name === 'startDate' &&
              updatedDate < new Date(values?.endDate) &&
              updatedErrors.endDate &&
              updatedErrors.endDate === expirationError
            ) {
              unset(updatedErrors, 'endDate');
              setErrors(updatedErrors);
            } else {
              setErrors(updatedErrors);
            }
            const dateFieldErrors = pick(updatedErrors, ['endDate']);
            if (!isEmpty(dateFieldErrors)) {
              setFieldValue('errorFields', dateFieldErrors);
            } else {
              setFieldValue('errorFields', null);
            }
            onUpdate(isEmpty(dateFieldErrors) && products.length);
          };

          useEffect(() => {
            if (errorFields) {
              setErrors(errorFields);
              onUpdate(isEmpty(errorFields));
            }
          }, [errorFields]);

          return (
            <Form noValidate>
              {errors.form && (
                <MessageBar
                  messageBarType={MessageBarType.error}
                  isMultiline
                  dismissButtonAriaLabel="Close"
                  nowrap
                >
                  {errors.form}
                </MessageBar>
              )}
              <SubModule disableAccordian>
                <SubModuleHeader>Bundle Details</SubModuleHeader>
                <SubModuleContent data-testid="bundleForm">
                  <StackGrid smCols={1} mdCols={2} lgCols={3} shrink>
                    <Field
                      name="bundleName"
                      label="Bundle Name"
                      component={TextField}
                      disabled={isSubmitting}
                      required
                    />
                    <Field
                      name="bundleId"
                      label="Bundle ID"
                      disabled={bundleId !== null}
                      component={TextField}
                      required
                    />
                    <Field
                      name="bundleType"
                      label="Bundle Type"
                      placeholder="Select"
                      options={bundleTypes}
                      component={Dropdown}
                      disabled={isSubmitting}
                      required
                    />
                    <Field
                      name="bundleDescription"
                      label="Description"
                      multiline
                      component={TextField}
                      disabled={isSubmitting}
                    />
                  </StackGrid>
                </SubModuleContent>
              </SubModule>

              <SubModule disableAccordian>
                <SubModuleHeader>Bundle Date Range</SubModuleHeader>
                <SubModuleContent>
                  <StackGrid smCols={1} mdCols={2} lgCols={3} shrink>
                    <Field
                      name="startDate"
                      label="Effective Start Date"
                      component={DatePicker}
                      onChange={handleDateFieldChange}
                      className={styles.datePicker}
                      required
                      disabled={isSubmitting}
                    />
                    <Field
                      name="endDate"
                      label="Expiration Date"
                      component={DatePicker}
                      onChange={handleDateFieldChange}
                      className={styles.datePicker}
                      required
                      disabled={isSubmitting}
                    />
                  </StackGrid>
                </SubModuleContent>
              </SubModule>

              <SubModule disableAccordian>
                <SubModuleHeader className={styles.moduleHeader}>
                  Bundled Products
                  {products.length > 0 && (
                    <>
                      {selectedProducts.length === 0 ? (
                        <TextButton
                          className={styles.selectButtonDefault}
                          data-testid="select-all"
                          onClick={() => handleSelectAllButton()}
                        >
                          {' '}
                          Select All
                        </TextButton>
                      ) : (
                        <TextButton
                          className={styles.selectButtonSelected}
                          data-testid="deselect-all"
                          onClick={() => handleDeselectAllButton()}
                        >
                          {' '}
                          Deselect All
                        </TextButton>
                      )}
                      <TextButton
                        onClick={() => handleRemoveProduct(selectedProducts, 'removeSelected')}
                        disabled={selectedProducts.length === 0}
                      >
                        Remove Selected
                      </TextButton>
                    </>
                  )}
                </SubModuleHeader>
                <SubModuleContent>
                  {products.length > 0 && (
                    <BundledProductList
                      items={products}
                      onRemove={handleRemoveProduct}
                      handleUpdatedProductList={handleUpdatedProductList}
                      disabled={isSubmitting}
                      className={styles.bundledProducts}
                      enableView
                      disableRowLink
                      showAllocation
                      allProductsSelected={allProductsSelected}
                      handleProductSelect={handleProductSelect}
                      showAllocationError={showAllocationError}
                      setShowAllocationError={setShowAllocationError}
                      selectedProductAllocations={selectedProducts}
                      isAddBundle
                    />
                  )}
                  <Link to={{ pathname: '/bundle/', bundleFormData: values }}>
                    <TextButton>Add Products</TextButton>
                  </Link>
                </SubModuleContent>
              </SubModule>
            </Form>
          );
        }}
      </Formik>
    );
  },
);

BundleForm.propTypes = {
  bundleId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onUpdate: PropTypes.func,
  onSuccess: PropTypes.func,
  showAllocationError: PropTypes.bool,
  setShowAllocationError: PropTypes.func,
};

BundleForm.defaultProps = {
  bundleId: null,
  onUpdate: () => {},
  onSuccess: () => {},
  showAllocationError: false,
  setShowAllocationError: () => {},
};

export default BundleForm;
